import { ReactElement, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { styled } from '@mui/material/styles'
import MuiTooltip from '@mui/material/Tooltip'
import { colorPalette } from '../../style/colorPalette'
import { WbsItemIcon } from '../../components/icons/WbsItemIcon'
import { WbsItemTypeVO } from '../../../domain/value-object/WbsItemTypeVO'
import { WbsItemStatus } from '../../../domain/entity/WbsItemEntity'
import { WbsItemBasic } from '../../../lib/functions/wbsItem'
import { WbsStatusLabel } from '../../components/data-display/WbsStatusLabel'
// カンバンのカードが使っている読み取り専用寄りの部品を流用する。KanbanContext・dnd-kit・
// 編集用ハンドラに依存していないことを確認済み（intl 単体・colorPalette・SelectOption 型のみ）。
import { UserAvatarsGroup } from '../../pages/KanbanV2/components/Card/UserAvatarsGroup'
import { SelectOption } from '../../pages/KanbanV2/Kanban'
import { dateV2Service } from '../../../domain/value-object/DateV2'
import { roundNumber } from '../../../utils/number'
import { AllState } from '../../../store'
import { useWbsItemDetailOnHover } from './hooks/useWbsItemDetailOnHover'

// UserAvatarsGroup・KanbanProgressIcon など、このリポジトリの「カード上のホバーで詳細を出す」
// 既存実装は素の MUI Tooltip に ReactNode を title として渡す作りに揃っている。新しい流儀
// （独自 Popper 等）を持ち込まず、同じ tooltipSlotProps の白地・ドロップシャドウの見た目を踏襲する。
const tooltipSlotProps = {
  popper: {
    sx: {
      filter: 'drop-shadow(0px 4px 12px rgba(123, 140, 170, 0.45))',
      // チャットは右下のフローティングパネル（zIndex: 1300）内でも使われる。それより確実に
      // 高くしないとパネルの裏に隠れてしまう。
      zIndex: 1400,
    },
  },
  tooltip: {
    sx: {
      backgroundColor: colorPalette.monotone[0],
      color: colorPalette.monotone[10],
      padding: 0,
      borderRadius: '8px',
      boxShadow: 'none',
      maxWidth: 320,
    },
  },
  arrow: {
    sx: { color: colorPalette.monotone[0] },
  },
}

const CardRoot = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '12px 14px',
  minWidth: 220,
  maxWidth: 320,
})

const HeaderRow = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 6,
})

const TypeIcon = styled(WbsItemIcon)({
  width: 18,
  height: 18,
  flexShrink: 0,
  marginTop: 1,
})

const DisplayName = styled('div')({
  fontSize: 13,
  fontWeight: 700,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
})

const ProjectRow = styled('div')({
  fontSize: 11,
  color: colorPalette.monotone[6],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const Divider = styled('div')({
  height: 1,
  backgroundColor: colorPalette.monotone[2],
  margin: '2px 0',
})

const PathText = styled('div')({
  fontSize: 11,
  color: colorPalette.monotone[5],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const DetailRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  fontSize: 12,
  color: colorPalette.monotone[7],
})

const UnavailableNote = styled('div')({
  fontSize: 11,
  color: colorPalette.monotone[5],
})

// KanbanProgressIcon の formatDateRange と同じ考え方（dateV2Service で月日だけの短い表記にする）
// だが、あちらは private 関数で import できないため同じ発想で組み直す。
const formatDate = (date: string | undefined): string => {
  const parsed = dateV2Service.fromString(date)
  return parsed ? dateV2Service.formatMonthAndDate(parsed) : ''
}

const toSelectOption = (
  member: { uuid: string; name: string; iconUrl: string } | undefined
): SelectOption | undefined =>
  member ? { ...member, value: member.uuid } : undefined

type Props = {
  code: string
  basic: WbsItemBasic
  children: ReactElement
}

/**
 * チップにホバーしたときに出す読み取り専用のカード（カンバンのカード相当の情報量）。
 *
 * 名称・種別・プロジェクトコードはチップの描画時点で取得済みの basic からすぐ出せるが、
 * ステータス・工数・担当者・予定期間は WbsItemDetail が要る。チップが 10 件並んだだけで
 * 10 件分の詳細取得が走らないよう、実際にホバーされたとき（Tooltip の onOpen）に初めて
 * useWbsItemDetailOnHover 経由で取得する。取得中は basic 由来の行だけを表示し、カード全体を
 * スピナーにはしない。取得できなかった場合もカード自体は保ち、その旨だけ添える。
 *
 * 進捗率（cumulation）は WbsItemDetail に存在しないため出さない。優先度もテナント固有の
 * マスタ（KanbanContext 経由でしか引けない）が要り、このカードのために新たに取得する
 * 価値と釣り合わないため出さない。
 */
export const WbsItemHoverCard = ({ code, basic, children }: Props) => {
  const intl = useIntl()
  const { lookup, requestLoad } = useWbsItemDetailOnHover(code, basic.uuid)
  // プロジェクトコードは人が読んで分かるものではないため名称で出す。参加プロジェクトは
  // ストアに載っているので追加の取得は不要（参加していないプロジェクトのアイテムは
  // そもそも詳細を引けないため、ここで名称が引けないケースは実質コード表示で足りる）。
  const projectName = useSelector(
    (state: AllState) =>
      state.project.assigned.find(project => project.code === basic.projectCode)
        ?.displayName
  )
  const wbsItemType = useMemo(
    () => new WbsItemTypeVO(basic.typeDto),
    [basic.typeDto]
  )
  const detail = lookup.status === 'resolved' ? lookup.detail : undefined

  const scheduleText = detail
    ? detail.scheduledDate?.startDate || detail.scheduledDate?.endDate
      ? `${formatDate(detail.scheduledDate.startDate)} ～ ${formatDate(detail.scheduledDate.endDate)}`
      : intl.formatMessage({ id: 'aiAgentChat.wbsItemCard.notSet' })
    : undefined

  const content = (
    <CardRoot>
      <HeaderRow>
        <TypeIcon type={wbsItemType} />
        <DisplayName>{basic.displayName}</DisplayName>
      </HeaderRow>
      <ProjectRow>{projectName ?? basic.projectCode}</ProjectRow>
      {detail && (
        <>
          <Divider />
          {detail.path && (
            <PathText title={detail.path}>{detail.path}</PathText>
          )}
          <DetailRow>
            <WbsStatusLabel status={WbsItemStatus[detail.status]} />
            <span>
              {intl.formatMessage(
                { id: 'popper.workload.tooltip' },
                {
                  scheduled: roundNumber(detail.estimatedHour ?? 0, 2),
                  actual: roundNumber(detail.actualHour ?? 0, 2),
                }
              )}
            </span>
          </DetailRow>
          <DetailRow>
            <span>{scheduleText}</span>
            <UserAvatarsGroup
              accountable={toSelectOption(detail.accountable)}
              responsible={toSelectOption(detail.responsible)}
              assignee={toSelectOption(detail.assignee)}
            />
          </DetailRow>
        </>
      )}
      {lookup.status === 'unavailable' && (
        <UnavailableNote>
          {intl.formatMessage({
            id: 'aiAgentChat.wbsItemCard.detailUnavailable',
          })}
        </UnavailableNote>
      )}
    </CardRoot>
  )

  return (
    <MuiTooltip
      title={content}
      placement="top"
      arrow={true}
      onOpen={requestLoad}
      slotProps={tooltipSlotProps}
    >
      {children}
    </MuiTooltip>
  )
}
